/**
 * Phase 8-F — Proposals API client (employee-scoped).
 *
 * Thin fetch wrapper matching `slack-api.ts` / `billing-api.ts` / `identity-api.ts`
 * conventions. RTK Query is intentionally NOT used here — kept consistent until
 * Phase 6 unifies everything.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}`.
 * Per CLAUDE.md, the token lives in Redux ONLY and is never persisted.
 *
 * Endpoints (Phase 8-C backend, employee-scoped). NOTE: The whole
 * `employee/tara/proposals/*` route group is mounted under the v2 API in
 * `tara-central-api/Modules/TaraActions/Routes/api/v2/api_v2.php` (the
 * `employee_auth` middleware group around lines 36–44). The original Phase
 * 12 client pointed these at /api/v1/* which 404s — fixed in Phase 12-A.
 *   GET    /api/v2/employee/tara/proposals             — paginated list
 *   GET    /api/v2/employee/tara/proposals/{id}        — single proposal
 *   POST   /api/v2/employee/tara/proposals/{id}/approve  body { notes? }
 *   POST   /api/v2/employee/tara/proposals/{id}/deny     body { reason }
 */
import { CENTRAL_API_BASE_URL_V2 } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

export type ProposalStatus =
  | 'proposed'
  | 'pending_review'
  | 'approved'
  | 'denied'
  | 'executing'
  | 'completed'
  | 'failed';

export type ProposalRisk = 'low' | 'medium' | 'high' | 'critical';

export type ProposalExecutionType =
  | 'platform_api'
  | 'official_mcp'
  | 'propose'
  // A client-report review item: approving publishes the report, denying
  // returns it for changes. Carries `metadata` (see ClientReportProposalMeta).
  | 'client_report';

// Metadata attached to `client_report` proposals. The backend also sends this
// object on other execution types when relevant, so it's typed loosely here
// and the client-report fields are optional.
export interface ClientReportProposalMeta {
  report_id?: string;
  report_version?: number;
  title?: string;
  brand?: string;
  shop_slug?: string;
  period_label?: string;
  created_by_type?: string;
  /** Deep link into the report workbench, e.g. "/client-reports/<id>". */
  report_review_path?: string;
  [key: string]: unknown;
}

export interface ProposalShop {
  slug: string;
  name: string;
}

export interface ProposalActionType {
  action_key: string;
  name: string;
}

// Phase 4 — creative-assets batch UI. Short-lived signed preview descriptor
// emitted by the backend on both list and detail proposal resources.
export interface CreativePreview {
  asset_id: string;
  kind: 'image' | 'video';
  mime_type: string;
  width: number | null;
  height: number | null;
  duration_ms: number | null;
  aspect_ratio: string | null;
  /** Short-lived signed URL; null when signing failed / asset unavailable. */
  preview_url: string | null;
}

export interface Proposal {
  id: string;
  shop_id: number;
  shop?: ProposalShop;
  action_type_id: string;
  action_type?: ProposalActionType;
  platform: string;
  category: string;
  entity_id: string;
  entity_name?: string;
  current_value: Record<string, any>;
  proposed_value: Record<string, any>;
  reasoning: string;
  confidence_score?: number;
  estimated_impact?: Record<string, any> | null;
  change_percentage?: number;
  risk_level: ProposalRisk;
  status: ProposalStatus;
  auto_approved: boolean;
  // The rule that approved the proposal, when one did.
  auto_approval_rule_id?: string | null;
  rule_name?: string | null;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  execution_result?: Record<string, any>;
  execution_error?: string;
  executed_at?: string;
  execution_type: ProposalExecutionType;
  // When a waiting proposal lapses; null for a client-report proposal.
  expires_at?: string | null;
  tool_name?: string;
  requested_by_employee_id?: string;
  // Free-form metadata the backend attaches to a proposal. For `client_report`
  // proposals this carries the report id / title / brand / period + the
  // workbench deep-link (see ClientReportProposalMeta).
  metadata?: ClientReportProposalMeta | Record<string, any> | null;
  // Phase 4 — creative-assets batch UI (backend feat/creative-assets).
  // Proposals created together share a non-null batch_id; creative launch
  // proposals carry signed preview descriptors for their assets.
  batch_id?: string | null;
  creative_previews?: CreativePreview[];
  created_at: string;
  updated_at: string;
}

export interface PaginationMeta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface ProposalListResponse {
  data: Proposal[];
  meta: PaginationMeta;
}

export interface ProposalListFilters {
  status?: string;
  shop_slug?: string;
  platform?: string;
  requested_by_employee_id?: string;
  /** Phase 4 — narrow the index to a single proposal batch. */
  batch_id?: string;
  page?: number;
  per_page?: number;
}

export interface ProposalReviewResponse {
  status: string;
  reviewed_at: string;
}

// ---------- Phase 4: batch types ----------

export interface BatchRollup {
  total: number;
  by_status: Record<string, number>;
  platforms: string[];
  shop_slugs: string[];
}

export interface BatchDetailResponse {
  batch_id: string;
  items: Proposal[];
  rollup: BatchRollup;
}

export type BatchItemResultStatus =
  | 'approved'
  | 'denied'
  | 'failed'
  | 'skipped';

export interface BatchItemResult {
  id: string;
  status: BatchItemResultStatus;
  reason?: string;
}

export interface BatchReviewSummary {
  approved?: number;
  denied?: number;
  failed?: number;
  skipped?: number;
}

export interface BatchReviewResponse {
  results: BatchItemResult[];
  summary: BatchReviewSummary;
}

// ---------- internal helpers ----------

function getAuthHeader(token?: string): Record<string, string> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (t) {
    headers['Authorization'] = `Bearer ${t}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[proposals-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  tokenOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(tokenOverride),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL_V2}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<T>(response);
}

function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function buildQuery(filters: ProposalListFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.shop_slug) params.set('shop_slug', filters.shop_slug);
  if (filters.platform) params.set('platform', filters.platform);
  if (filters.requested_by_employee_id) {
    params.set('requested_by_employee_id', filters.requested_by_employee_id);
  }
  if (filters.batch_id) params.set('batch_id', filters.batch_id);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.per_page) params.set('per_page', String(filters.per_page));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ---------- public API ----------

/**
 * List proposals for the current employee. Returns a `{ data, meta }`
 * envelope so the UI can render pagination directly.
 */
export async function listProposals(
  filters: ProposalListFilters = {},
  token?: string
): Promise<ProposalListResponse> {
  const raw = await request<
    ProposalListResponse | { data?: ProposalListResponse }
  >(`employee/tara/proposals${buildQuery(filters)}`, { method: 'GET' }, token);

  // Some backends double-wrap as `{ data: { data: [...], meta: {...} } }`.
  // Detect that case and unwrap once; otherwise return raw.
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in raw &&
    Array.isArray((raw as ProposalListResponse).data) &&
    'meta' in raw
  ) {
    return raw as ProposalListResponse;
  }

  const unwrapped = unwrap<ProposalListResponse>(
    raw as ProposalListResponse | { data?: ProposalListResponse }
  );
  // Final defensive default
  return {
    data: unwrapped?.data ?? [],
    meta: unwrapped?.meta ?? {
      total: 0,
      per_page: filters.per_page ?? 25,
      current_page: filters.page ?? 1,
      last_page: 1,
    },
  };
}

export async function getProposal(
  id: string,
  token?: string
): Promise<Proposal> {
  const raw = await request<Proposal | { data?: Proposal }>(
    `employee/tara/proposals/${encodeURIComponent(id)}`,
    { method: 'GET' },
    token
  );
  return unwrap<Proposal>(raw);
}

export async function approveProposal(
  id: string,
  notes: string | undefined,
  token?: string
): Promise<ProposalReviewResponse> {
  const body: Record<string, unknown> = {};
  if (notes && notes.trim()) body.notes = notes.trim();
  const raw = await request<
    ProposalReviewResponse | { data?: ProposalReviewResponse }
  >(
    `employee/tara/proposals/${encodeURIComponent(id)}/approve`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    token
  );
  return unwrap<ProposalReviewResponse>(raw);
}

// ---------- Phase 4: batch endpoints ----------
//
// Mounted alongside the employee proposals group in the v2 API
// (tara-central-api feat/creative-assets):
//   GET  /api/v2/employee/tara/actions/batches/{batchId}
//   POST /api/v2/employee/tara/actions/batches/{batchId}/approve  { notes? }
//   POST /api/v2/employee/tara/actions/batches/{batchId}/deny     { notes? }

export async function getBatch(
  batchId: string,
  token?: string
): Promise<BatchDetailResponse> {
  const raw = await request<
    BatchDetailResponse | { data?: BatchDetailResponse }
  >(
    `employee/tara/actions/batches/${encodeURIComponent(batchId)}`,
    { method: 'GET' },
    token
  );
  return unwrap<BatchDetailResponse>(raw);
}

async function reviewBatch(
  batchId: string,
  verb: 'approve' | 'deny',
  notes: string | undefined,
  token?: string
): Promise<BatchReviewResponse> {
  const body: Record<string, unknown> = {};
  if (notes && notes.trim()) body.notes = notes.trim();
  const raw = await request<
    BatchReviewResponse | { data?: BatchReviewResponse }
  >(
    `employee/tara/actions/batches/${encodeURIComponent(batchId)}/${verb}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    token
  );
  const unwrapped = unwrap<BatchReviewResponse>(raw);
  // Defensive defaults — a malformed response should not crash the results UI.
  return {
    results: Array.isArray(unwrapped?.results) ? unwrapped.results : [],
    summary: unwrapped?.summary ?? {},
  };
}

export function approveBatch(
  batchId: string,
  notes?: string,
  token?: string
): Promise<BatchReviewResponse> {
  return reviewBatch(batchId, 'approve', notes, token);
}

export function denyBatch(
  batchId: string,
  notes?: string,
  token?: string
): Promise<BatchReviewResponse> {
  return reviewBatch(batchId, 'deny', notes, token);
}

// ---------- Phase 4: cross-platform replication ----------
//
// POST /api/v2/employee/tara/actions/proposals/{id}/replicate
//   body { platforms: string[], notes? }
// Valid ONLY for creative proposals (action_key ends '.ad.create' /
// '.ad.create_rsa') AFTER approve succeeded (status approved+). Platform
// keys: 'facebook' | 'tiktok' | 'snapchat' | 'google' | 'linkedin'.
// Standard { success, message, data } envelope; data.results is per-platform:
//   - proposal_created : an equivalent proposal was auto-created (appears in
//                        the queue on the next refresh)
//   - request_queued   : Tara will prepare it and file a new proposal later
//   - skipped          : not replicated; `reason` says why (copy exceeds
//                        TikTok's 100-char limit, platform not connected,
//                        Google requires text-ad rework, ...)
//   - proposal_exists  : an identical proposal is already waiting for
//                        approval on that platform; nothing new was filed

export type ReplicationResultStatus =
  | 'proposal_created'
  | 'request_queued'
  | 'proposal_exists'
  | 'skipped';

export interface ReplicationResult {
  platform: string;
  status: ReplicationResultStatus;
  proposal_id?: string;
  reason?: string;
}

export interface ReplicateResponse {
  results: ReplicationResult[];
}

export async function replicateProposal(
  id: string,
  platforms: string[],
  notes?: string,
  token?: string
): Promise<ReplicateResponse> {
  const body: Record<string, unknown> = { platforms };
  if (notes && notes.trim()) body.notes = notes.trim();
  const raw = await request<ReplicateResponse | { data?: ReplicateResponse }>(
    `employee/tara/actions/proposals/${encodeURIComponent(id)}/replicate`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    token
  );
  const unwrapped = unwrap<ReplicateResponse>(raw);
  // Defensive default — a malformed response should not crash the results UI.
  return {
    results: Array.isArray(unwrapped?.results) ? unwrapped.results : [],
  };
}

export async function denyProposal(
  id: string,
  reason: string,
  token?: string
): Promise<ProposalReviewResponse> {
  const raw = await request<
    ProposalReviewResponse | { data?: ProposalReviewResponse }
  >(
    `employee/tara/proposals/${encodeURIComponent(id)}/deny`,
    {
      method: 'POST',
      body: JSON.stringify({ reason }),
    },
    token
  );
  return unwrap<ProposalReviewResponse>(raw);
}
